import { validatePathParams } from '@/utils/hooks/api/safePathParams';
import { getAPIRoute } from '@/utils/routing/apiRoutes';
import { useSnackBarContext } from '@/utils/snackbar';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect } from 'react';

export default function useGet(endpoint, config) {
  const { updateSnack } = useSnackBarContext();

  let baseEndpoint = endpoint;
  let pathParamsValid = true;

  if (config?.pathParams) {
    const { valid, path } = validatePathParams(
      endpoint,
      config.pathParams,
      config.pathParamValidators
    );
    pathParamsValid = valid;
    if (valid && path) {
      baseEndpoint = path;
    }
  }

  // Handle query parameters. We perform this manually rather than letting axios do it
  // so that we can include the query params in the react-query key.
  let fullEndpoint = '/api' + getAPIRoute(baseEndpoint);
  if (config?.params) {
    let queryParams = [];
    for (let param in config.params) {
      queryParams.push(param + '=' + encodeURIComponent(config.params[param]));
    }
    fullEndpoint = `${fullEndpoint}?${queryParams.join('&')}`;
  }

  const key = fullEndpoint + (config?.optionalKey || '');

  const query = useQuery([key], () => axios.get(fullEndpoint), {
    enabled: pathParamsValid && config?.fireOnMount && !config.skip,
    refetchOnWindowFocus: false,
    refetchInterval: config?.refetchInterval,
    keepPreviousData: config?.keepPreviousData,
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!query.isFetching) {
      if (config?.successMessage && query.isSuccess) {
        updateSnack({
          severity: 'success',
          message: config.successMessage,
        });
      }
      if (config?.errorMessage && query.isError) {
        updateSnack({
          severity: 'error',
          message: config.errorMessage,
        });
      }
    }
  }, [query.isFetching, query.isSuccess, query.isError]);

  const refetchWithNewParams = (params) => {
    let refetchEndpoint = '/api' + getAPIRoute(baseEndpoint);
    if (params) {
      let queryParams = [];
      for (let param in params) {
        queryParams.push(param + '=' + encodeURIComponent(params[param]));
      }
      refetchEndpoint = `${refetchEndpoint}?${queryParams.join('&')}`;
    }
    return queryClient.refetchQueries([refetchEndpoint]);
  };

  // Invalidate the cached query for this endpoint. This marks it stale but does not force an immediate network request.
  const revalidateCache = () => {
    return queryClient.invalidateQueries([key]);
  };

  // Invalidate then, if not already fetching, immediately refetch to get fresh data.
  const revalidateAndFetch = async () => {
    await queryClient.invalidateQueries([key]); // mark stale
    if (query.isFetching) return query; // don't trigger concurrent fetches
    return query.refetch(); // explicit fetch after invalidation
  };

  // Automatic scheduled revalidation logic.
  // If config.revalidateInterval is provided, run on that interval.
  // Two strategies:
  // 1. 'invalidate' -> only mark the cache stale (no immediate fetch). Consumer components may refetch later.
  // 2. anything else -> invalidate then actively refetch right away.
  useEffect(() => {
    if (!config?.revalidateInterval) return; // nothing to do if no interval specified

    const interval = setInterval(() => {
      if (query.isFetching) return; // skip if a fetch is already in progress
      if (config.revalidateStrategy === 'invalidate') {
        // Only mark the cached data as stale; does not trigger a fetch immediately.
        revalidateCache();
      } else {
        // Invalidate and then explicitly fetch fresh data.
        revalidateAndFetch();
      }
    }, config.revalidateInterval);

    return () => clearInterval(interval);
  }, [
    config?.revalidateInterval,
    config?.revalidateStrategy,
    key,
    query.isFetching,
  ]);

  return {
    request: query,
    fire: query.refetch,
    refetchWithNewParams,
    revalidateCache,
    revalidateAndFetch,
    isLoading: query.isLoading,
    isError: query.isError,
    data: query.data?.data?.body || query.data?.data || [],
    error: query.error?.response?.data?.body || query.error?.error,
    isFetching: query.isFetching,
  };
}
